import {SchoolClassCheckIn, Survey, TeamSurvey, TeamSurveyStep} from "models"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import AppHistory from "shared/history"
import LoadingSpinner from "components/layout/loading-spinner"
import memo from "set-state-compare/build/memo"
import React from "react"
import RoutesPlay from "shared/routes-play"
import setLayout from "shared/set-layout"
import SurveysLayout from "components/surveys/layout"
import SurveysSelectedAttributes from "components/surveys/selected-attributes"
import SurveyStepsPresentation from "components/survey-steps/presentation"
import useHiddenTawkWidget from "shared/use-hidden-tawk-widget"
import useModel from "@kaspernj/api-maker/build/use-model"

const teamSurveyStepPreloads = ["team_survey_step_elements.answers"]
const teamSurveyStepSelects = Object.assign({}, SurveysSelectedAttributes.select)

teamSurveyStepSelects["TeamSurveyStep"] = ["id", "teamSurveyId"]
teamSurveyStepSelects["TeamSurveyStepElement"] = ["id", "parsedContent", "parsedMainContent", "state", "surveyStepElementId", "teamSurveyStepId"]
teamSurveyStepSelects["TeamSurveyStepElementAnswer"] = ["id", "surveyStepElementOptionId"]

for (const surveyStepPreload of SurveysSelectedAttributes.preload) {
  teamSurveyStepPreloads.push(
    `survey_step.${surveyStepPreload}`
  )
}

/**
 * @typedef {object} RoutesPlaySchoolClassCheckInsTeamSurveysSurveyStepsShowProps
 * @property {unknown} match
 */

/** @typedef {object} RoutesPlaySchoolClassCheckInsTeamSurveysSurveyStepsShowState */

/**
 * @extends {ShapeComponent<RoutesPlaySchoolClassCheckInsTeamSurveysSurveyStepsShowProps, RoutesPlaySchoolClassCheckInsTeamSurveysSurveyStepsShowState>}
 */
class RoutesPlaySchoolClassCheckInsTeamSurveysSurveyStepsShow extends ShapeComponent {
  setup() {
    setLayout("play")

    const {teamSurvey, teamSurveyId} = useModel(TeamSurvey, {
      match: this.p.match,
      select: {
        TeamSurvey: ["id"]
      }
    })
    const {teamSurveyStep, teamSurveyStepId} = useModel(TeamSurveyStep, {
      match: this.p.match,
      preload: teamSurveyStepPreloads,
      select: teamSurveyStepSelects
    })
    const {schoolClassCheckIn, schoolClassCheckInId} = useModel(SchoolClassCheckIn, {
      match: this.p.match,
      preload: ["school_class"],
      select: {
        SchoolClass: ["id", "schoolId"],
        SchoolClassCheckIn: ["id"]
      }
    })

    this.teamSurvey = teamSurvey
    this.teamSurveyId = teamSurveyId
    this.teamSurveyStep = teamSurveyStep
    this.teamSurveyStepId = teamSurveyStepId
    this.schoolClassCheckIn = schoolClassCheckIn
    this.schoolClassCheckInId = schoolClassCheckInId

    useHiddenTawkWidget()
  }

  render() {
    const {onAnswersSubmitted, schoolClassCheckIn, teamSurvey, teamSurveyId, teamSurveyStep, teamSurveyStepId} = this.tt
    const schoolClass = schoolClassCheckIn?.schoolClass()
    const surveyStep = teamSurveyStep?.surveyStep()
    const teamSurveyMatchesRoute = teamSurvey?.id() == teamSurveyId
    const teamSurveyStepMatchesRoute = teamSurveyStep?.id() == teamSurveyStepId
    const teamSurveyStepMatchesTeamSurvey = teamSurvey && teamSurveyStep && teamSurvey.id() == teamSurveyStep.teamSurveyId()
    const canRenderSurveyStepPresentation = schoolClass &&
      schoolClassCheckIn &&
      teamSurvey &&
      teamSurveyStep &&
      teamSurveyMatchesRoute &&
      teamSurveyStepMatchesRoute &&
      teamSurveyStepMatchesTeamSurvey

    if (schoolClassCheckIn && !schoolClass) {
      throw new Error(`Couldn't load school class for school class check in ${schoolClassCheckIn.id()}`)
    }

    return (
      <SurveysLayout
        dataSet={{
          route: "play/school-class-check-in/team-surveys/survey-steps/show",
          routeTeamSurveyId: teamSurveyId,
          routeTeamSurveyStepId: teamSurveyStepId,
          modelTeamSurveyId: teamSurvey?.id(),
          modelTeamSurveyStepId: teamSurveyStep?.id(),
          surveyStepPresentationReady: canRenderSurveyStepPresentation,
          surveyStepId: surveyStep?.id(),
          teamSurveyStepId: teamSurveyStep?.id()
        }}
        headTitle={Survey.modelName().human()}
        layout="play"
        schoolClass={schoolClass}
      >
        {!canRenderSurveyStepPresentation &&
          <LoadingSpinner />
        }
        {canRenderSurveyStepPresentation &&
          <SurveyStepsPresentation
            key={`${teamSurveyId}-${teamSurveyStepId}`}
            onAnswersSubmitted={onAnswersSubmitted}
            schoolClass={schoolClass}
            surveyStep={surveyStep}
            teamSurvey={teamSurvey}
            teamSurveyStep={teamSurveyStep}
          />
        }
      </SurveysLayout>
    )
  }

  onAnswersSubmitted = ({nextTeamSurveyStep}) => {
    const {schoolClassCheckInId, teamSurveyId} = this.tt

    if (nextTeamSurveyStep) {
      if (!nextTeamSurveyStep.id()) throw new Error("No ID on nextTeamSurveyStep")

      AppHistory.push(RoutesPlay.schoolClassCheckInTeamSurveyTeamSurveyStepPath(schoolClassCheckInId, teamSurveyId, nextTeamSurveyStep.id()))
    } else {
      AppHistory.push(RoutesPlay.schoolClassCheckInTeamSurveyCompletedPath(schoolClassCheckInId, teamSurveyId))
    }
  }
}

export default memo(shapeComponent(RoutesPlaySchoolClassCheckInsTeamSurveysSurveyStepsShow))
